/**
 * React Query mutation hook for commission data modifications
 *
 * Handles updating commission data per benefit plan with automatic
 * transformation to API format and cache invalidation.
 */

import { useRef } from "react";
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";

import { transformCommissionsToApi } from "./commissionHelpers";
import { queryKeys } from "./queries";
import { useApiClient } from "./useApiClient";

/**
 * Commission update request with frontend field format
 */
export interface CommissionUpdateRequest {
  rfpId: string;
  benefitPlanId: string;
  currentCommissionType?: string;
  currentCommissionAmount?: number | { first: number; thereafter: number };
  requestedCommissionType?: string;
  requestedCommissionAmount?: number | { first: number; thereafter: number };
}

/**
 * Hook to update commission data for a benefit plan
 *
 * Automatically transforms commission data to API format (including heaped amount conversion)
 * and invalidates RFP queries on success.
 *
 * @param options - React Query mutation options
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * const updateCommission = useUpdateCommission({
 *   onSuccess: () => {
 *     toast.success('Commission updated');
 *   },
 *   onError: (error) => {
 *     toast.error(`Failed: ${error.message}`);
 *   }
 * });
 *
 * updateCommission.mutate({
 *   rfpId: 'rfp-123',
 *   benefitPlanId: 'plan-456',
 *   currentCommissionType: 'percentage',
 *   currentCommissionAmount: 5,
 *   requestedCommissionType: 'heaped',
 *   requestedCommissionAmount: { first: 7, thereafter: 4 }
 * });
 * ```
 */
export const useUpdateCommission = (
  options?: UseMutationOptions<void, Error, CommissionUpdateRequest>,
): UseMutationResult<void, Error, CommissionUpdateRequest> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApiClient();
  const planQueuesRef = useRef<Map<string, Promise<void>>>(new Map());

  if (!axiosInstance) {
    throw new Error("Commission API client is not available");
  }

  return useMutation({
    mutationFn: (request: CommissionUpdateRequest) => {
      const { benefitPlanId } = request;
      const queuedAhead =
        planQueuesRef.current.get(benefitPlanId) ?? Promise.resolve();
      const next = queuedAhead.then(() =>
        sendCommissionUpdate(axiosInstance, request),
      );

      planQueuesRef.current.set(
        benefitPlanId,
        next.catch(() => undefined),
      );

      return next;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.rfp(variables.rfpId),
      });
    },
    ...options,
  });
};

async function sendCommissionUpdate(
  axiosInstance: NonNullable<ReturnType<typeof useApiClient>>,
  request: CommissionUpdateRequest,
): Promise<void> {
  const { rfpId, benefitPlanId, ...commissionFields } = request;

  const apiData = transformCommissionsToApi(
    commissionFields.currentCommissionType,
    commissionFields.currentCommissionAmount,
    commissionFields.requestedCommissionType,
    commissionFields.requestedCommissionAmount,
  );

  const body: Record<string, string | number | number[]> = {};

  if (apiData) {
    if (apiData.currentType !== undefined) {
      body["currentType"] = apiData.currentType;
    }
    if (apiData.currentAmount !== undefined) {
      body["currentAmount"] = apiData.currentAmount;
    }
    if (apiData.currentHeapedAmount !== undefined) {
      body["currentHeapedAmount"] = apiData.currentHeapedAmount;
    }
    if (apiData.requestedType !== undefined) {
      body["requestedType"] = apiData.requestedType;
    }
    if (apiData.requestedAmount !== undefined) {
      body["requestedAmount"] = apiData.requestedAmount;
    }
    if (apiData.requestedHeapedAmount !== undefined) {
      body["requestedHeapedAmount"] = apiData.requestedHeapedAmount;
    }
  }

  await axiosInstance.patch(
    `btx/v1/rfps/${rfpId}/commissions/${benefitPlanId}`,
    body,
  );
}
